import * as i0 from '@angular/core';
import { Directive, Input, HostListener, Pipe, Inject, EventEmitter, Component, ChangeDetectionStrategy, Output, Injectable, NgModule } from '@angular/core';
import moment from 'moment';
import * as _ from 'lodash';
import * as i1 from '@angular/common';
import { CommonModule } from '@angular/common';
import { throwError, map, of } from 'rxjs';
import moment$1 from 'moment/moment';
import * as i1$1 from '@angular/common/http';

class AerialSearchHistorySlice {
    constructor() {
        this.goingDate = moment();
        this.returningDate = moment();
        this.origin = '';
        this.destination = '';
        this.sliceOrder = 0;
    }
}

class ApiResource {
    getApiDateFields() {
        return [];
    }
    getApiDatetimeFields() {
        return [];
    }
    getApiNumericFields() {
        return [];
    }
    getApiBooleanFields() {
        return [];
    }
    getApiDurationFields() {
        return [];
    }
    fromApi(apiData) {
        for (let key in apiData) {
            if (!_.isObject(apiData[key])) {
                this.assignFieldValue(key, apiData[key]);
            }
        }
    }
    assignFieldValue(key, value) {
        if (this.getApiDateFields().includes(key) && value) {
            this.setDateField(key, value);
        }
        else if (this.getApiDatetimeFields().includes(key) && value) {
            this.setDatetimeField(key, value);
        }
        else if (this.getApiNumericFields().includes(key) && value) {
            this.setNumericField(key, value);
        }
        else if (this.getApiBooleanFields().includes(key) && value) {
            this.setBooleanField(key, value);
        }
        else if (this.getApiDurationFields().includes(key) && value) {
            this.setDurationField(key, value);
        }
        else {
            Object.defineProperty(this, key, {
                value: value,
                writable: true,
                enumerable: true,
            });
        }
    }
    setDateField(key, value) {
        Object.defineProperty(this, key, {
            value: moment(value, moment.HTML5_FMT.DATE),
            writable: true,
            enumerable: true,
        });
    }
    setDatetimeField(key, value) {
        Object.defineProperty(this, key, {
            value: moment(value, moment.HTML5_FMT.DATETIME_LOCAL_SECONDS),
            writable: true,
            enumerable: true,
        });
    }
    setNumericField(key, value) {
        Object.defineProperty(this, key, {
            value: Number(value),
            writable: true,
            enumerable: true,
        });
    }
    setBooleanField(key, value) {
        Object.defineProperty(this, key, {
            value: this._convertToBoolean(value),
            writable: true,
            enumerable: true,
        });
    }
    setDurationField(key, value) {
        Object.defineProperty(this, key, {
            value: moment.duration(value),
            writable: true,
            enumerable: true,
        });
    }
    _convertToBoolean(value) {
        if ((_.isString(value) && value.toLocaleLowerCase() === 'false') ||
            value === '0' ||
            value === 0) {
            return false;
        }
        else if (_.isNull(value)) {
            return null;
        }
        else {
            return Boolean(value);
        }
    }
}

class ApiSearchHistory extends ApiResource {
    constructor(apiData) {
        super();
        this.id = '';
        this.userId = 0;
        this.origin = '';
        this.originCityName = '';
        this.destination = '';
        this.destinationCityName = '';
        this.destinationCountry = '';
        this.type = '';
        this.isInternational = false;
        this.adultQuantity = 0;
        this.childrenQuantity = 0;
        this.infantQuantity = 0;
        this.createdAt = moment();
        this.updatedAt = moment();
        this.fromApi(apiData);
    }
    getApiDateFields() {
        return ['departureDate', 'arrivalDate', 'createdAt', 'updatedAt'];
    }
    getApiNumericFields() {
        return ['userId', 'adultQuantity', 'childrenQuantity', 'infantQuantity'];
    }
    getApiBooleanFields() {
        return ['isInternational'];
    }
    fromApi(apiData) {
        super.fromApi(Object.assign({
            userId: apiData?.user_id,
            departureDate: apiData?.departure_date,
            arrivalDate: apiData?.arrival_date,
            isInternational: apiData?.is_international,
            adultQuantity: apiData?.adult_quantity,
            childrenQuantity: apiData?.children_quantity,
            infantQuantity: apiData?.infant_quantity,
        }, apiData ?? {}));
    }
    toApi() {
        return {
            id: this.id,
            user_id: this.userId,
            origin: this.origin,
            destination: this.destination,
            departure_date: this.departureDate.format(moment.HTML5_FMT.DATE),
            arrival_date: this.arrivalDate instanceof moment
                ? this.arrivalDate?.format(moment.HTML5_FMT.DATE)
                : '',
            type: this.type,
            is_international: this.isInternational,
            adult_quantity: this.adultQuantity,
            children_quantity: this.childrenQuantity,
            infant_quantity: this.infantQuantity,
            created_at: this.createdAt.format(moment.HTML5_FMT.DATE),
            updated_at: this.updatedAt.format(moment.HTML5_FMT.DATE),
        };
    }
}

var ZupperProduct;
(function (ZupperProduct) {
    ZupperProduct["AERIAL"] = "AERIAL";
    ZupperProduct["HOTEL"] = "HOTEL";
    ZupperProduct["PACKAGES"] = "PACKAGES";
    ZupperProduct["INSURANCE"] = "INSURANCE";
})(ZupperProduct || (ZupperProduct = {}));

class TravelerGroup {
    constructor() {
        this.travelers = [];
    }
    addTraveler(traveler) {
        this.travelers.push(traveler);
    }
    getTotalTravelers(travelerType) {
        return this.travelers.filter(t => t.travelerType === travelerType).length;
    }
}

var TRAVELER_TYPE;
(function (TRAVELER_TYPE) {
    TRAVELER_TYPE["ADULT"] = "ADULT";
    TRAVELER_TYPE["CHILD"] = "CHILD";
    TRAVELER_TYPE["INFANT"] = "INFANT";
})(TRAVELER_TYPE || (TRAVELER_TYPE = {}));

class AerialSearchHistory extends ApiSearchHistory {
    constructor(apiData) {
        super(apiData);
        this.slices = [];
        this.travelerGroups = [];
        this.createSlices();
        this.createTravelerGroups();
        this.product = ZupperProduct.AERIAL;
    }
    static fromAerialSearchHistory(searchHistory) {
        const history = new AerialSearchHistory(searchHistory);
        const firstSlice = searchHistory?.slices[0];
        history.origin = firstSlice?.origin ?? '';
        history.destination = firstSlice?.destination ?? '';
        history.departureDate = moment(firstSlice?.goingDate);
        history.arrivalDate = searchHistory?.slices[1]?.goingDate
            ? moment(searchHistory?.slices[1]?.goingDate)
            : undefined;
        history.slices = searchHistory?.slices ?? history.slices;
        history.travelerGroups =
            searchHistory?.travelerGroups ?? history.travelerGroups;
        return history;
    }
    createSlices() {
        if (this.departureDate instanceof moment && this.departureDate?.isValid()) {
            const firstSlice = new AerialSearchHistorySlice();
            firstSlice.goingDate = this.departureDate;
            firstSlice.origin = this.origin;
            firstSlice.destination = this.destination;
            firstSlice.sliceOrder = 0;
            this.addSlice(firstSlice);
        }
        if (this.type === 'roundTrip' &&
            this.arrivalDate instanceof moment &&
            this.arrivalDate?.isValid()) {
            const secondSlice = new AerialSearchHistorySlice();
            secondSlice.goingDate = this.arrivalDate;
            secondSlice.origin = this.destination;
            secondSlice.destination = this.origin;
            secondSlice.sliceOrder = 1;
            this.addSlice(secondSlice);
        }
    }
    createTravelerGroups() {
        if (this.adultQuantity > 0) {
            const adultGroup = new TravelerGroup();
            for (let i = 1; i <= this.adultQuantity; i++) {
                adultGroup.addTraveler({ travelerType: TRAVELER_TYPE.ADULT });
            }
            this.addTravelerGroup(adultGroup);
        }
        if (this.childrenQuantity > 0) {
            const childrenGroup = new TravelerGroup();
            for (let i = 1; i <= this.childrenQuantity; i++) {
                childrenGroup.addTraveler({ travelerType: TRAVELER_TYPE.CHILD });
            }
            this.addTravelerGroup(childrenGroup);
        }
        if (this.infantQuantity > 0) {
            const infantGroup = new TravelerGroup();
            for (let i = 1; i <= this.infantQuantity; i++) {
                infantGroup.addTraveler({ travelerType: TRAVELER_TYPE.INFANT });
            }
            this.addTravelerGroup(infantGroup);
        }
    }
    addTravelerGroup(group) {
        this.travelerGroups.push(group);
    }
    addSlice(slice) {
        this.slices.push(slice);
    }
}

class ImageFallbackDirective {
    constructor(element, renderer) {
        this.element = element;
        this.renderer = renderer;
    }
    onImageError() {
        if (this.latestImage === this.imageFallback)
            return;
        else if (this.imageFallback) {
            this.latestImage = this.imageFallback;
            this.setProperty('src', this.imageFallback);
            this.setProperty('style', 'height: 32px; width: 32px;');
        }
    }
    setProperty(property, value) {
        this.renderer.setProperty(this.element.nativeElement, property, value);
    }
}
ImageFallbackDirective.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: ImageFallbackDirective, deps: [{ token: i0.ElementRef }, { token: i0.Renderer2 }], target: i0.ɵɵFactoryTarget.Directive });
ImageFallbackDirective.ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "14.0.0", version: "15.2.10", type: ImageFallbackDirective, selector: "[imageFallback]", inputs: { imageFallback: "imageFallback" }, host: { listeners: { "error": "onImageError()" } }, ngImport: i0 });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: ImageFallbackDirective, decorators: [{
            type: Directive,
            args: [{ selector: '[imageFallback]' }]
        }], ctorParameters: function () { return [{ type: i0.ElementRef }, { type: i0.Renderer2 }]; }, propDecorators: { imageFallback: [{
                type: Input
            }], onImageError: [{
                type: HostListener,
                args: ['error']
            }] } });

class DestinationImageUrlPipe {
    constructor(config) {
        this.config = config;
    }
    transform(destinationCityName, isInternational) {
        if (!(this.config?.zupperImagesBaseUrl && destinationCityName))
            return '';
        let baseUrl = this.config.zupperImagesBaseUrl;
        if (baseUrl.at(-1) !== '/')
            baseUrl = baseUrl.concat('/');
        const nationality = isInternational ? 'Internacionais' : 'Nacionais';
        const cityName = destinationCityName
            ?.normalize('NFD')
            .replace(/[\u0300-\u036f]/g, '')
            .split(' ')
            .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
            .join('');
        return `${baseUrl}${nationality}/${cityName}01.jpg
  `;
    }
}
DestinationImageUrlPipe.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: DestinationImageUrlPipe, deps: [{ token: 'SEARCH_HISTORY_CONFIG' }], target: i0.ɵɵFactoryTarget.Pipe });
DestinationImageUrlPipe.ɵpipe = i0.ɵɵngDeclarePipe({ minVersion: "14.0.0", version: "15.2.10", ngImport: i0, type: DestinationImageUrlPipe, name: "destinationImageUrl" });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: DestinationImageUrlPipe, decorators: [{
            type: Pipe,
            args: [{
                    name: 'destinationImageUrl',
                }]
        }], ctorParameters: function () { return [{ type: undefined, decorators: [{
                    type: Inject,
                    args: ['SEARCH_HISTORY_CONFIG']
                }] }]; } });

class AerialSearchHistoryComponent {
    constructor(changeDetectionRef) {
        this.changeDetectionRef = changeDetectionRef;
        this.goingDate = '';
        this.returningDate = '';
        this.destination = '';
        this.origin = '';
        this.aerialSearchHistory = new AerialSearchHistory();
        this.continueSearch = new EventEmitter();
        this.continueService = new EventEmitter();
        moment.locale('pt-BR');
    }
    ngOnInit() {
        this.updateFields();
    }
    ngOnChanges(changes) {
        if (changes['aerialSearchHistory']) {
            this.updateFields(changes['aerialSearchHistory'].currentValue);
        }
    }
    updateFields(history = this.aerialSearchHistory) {
        this.goingDate = this.formatDate(history?.departureDate);
        this.returningDate = this.formatDate(history?.arrivalDate);
        this.destination =
            history?.destinationCityName || history?.destination || '';
        this.origin = history?.originCityName || history?.origin || '';
        this.changeDetectionRef.detectChanges();
    }
    formatDate(date) {
        if (!(date && date.isValid()))
            return '';
        const dayAndMonth = date
            .format('ll')
            .replaceAll('de ', '')
            .split(' ')
            .slice(0, 2)
            .join(' ');
        const year = date.format('YY');
        return `${dayAndMonth} ${year}`;
    }
    get isOneWay() {
        return this.aerialSearchHistory?.type === 'oneWay';
    }
    get isRoundTrip() {
        return this.aerialSearchHistory?.type === 'roundTrip';
    }
    linkClick() {
        if (this.aerialSearchHistory?.isInternational)
            this.continueService.emit();
        else
            this.continueSearch.emit();
    }
}
AerialSearchHistoryComponent.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: AerialSearchHistoryComponent, deps: [{ token: i0.ChangeDetectorRef }], target: i0.ɵɵFactoryTarget.Component });
AerialSearchHistoryComponent.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "15.2.10", type: AerialSearchHistoryComponent, selector: "aerial-search-history", inputs: { aerialSearchHistory: "aerialSearchHistory" }, outputs: { continueSearch: "continueSearch", continueService: "continueService" }, usesOnChanges: true, ngImport: i0, template: "<div class=\"aerial-search-history-card\">\n    <div class=\"aerial-search-history-card-image\">\n        <img [src]=\"destination | destinationImageUrl : aerialSearchHistory?.destinationCountry !== 'Brasil'\"\n            imageFallback=\"assets/images/image-fallback.webp\" style=\"height: 100%; width: 100%;\" />\n    </div>\n    <div class=\"aerial-search-history-card-information\">\n        <div class=\"aerial-search-history-card-flight-information\">\n            <div class=\"aerial-search-history-card-row\">\n                <svg width=\"17\" height=\"18\" viewBox=\"0 0 17 18\" fill=\"none\" xmlns=\"http://www.w3.org/2000/svg\">\n                    <mask id=\"mask0_1362_130\" style=\"mask-type:alpha\" maskUnits=\"userSpaceOnUse\" x=\"0\" y=\"0\" width=\"17\"\n                        height=\"18\">\n                        <rect y=\"0.5\" width=\"17\" height=\"17\" fill=\"#D9D9D9\" />\n                    </mask>\n                    <g mask=\"url(#mask0_1362_130)\">\n                        <path\n                            d=\"M0.553255 15.9672C0.553255 15.7804 0.704702 15.629 0.891521 15.629H6.14204C6.32885 15.629 6.4803 15.7804 6.4803 15.9672C6.4803 16.154 6.32885 16.3055 6.14204 16.3055H0.891521C0.704702 16.3055 0.553255 16.154 0.553255 15.9672ZM16.9581 6.78279C16.7821 6.11218 16.0863 5.70981 15.4157 5.89423L10.9645 7.08457L5.16372 1.69452L3.56263 2.12203L7.03305 8.1324L2.86687 9.24729L1.21548 7.95636L0 8.28329L1.52564 10.9322L2.17111 12.0471L3.51233 11.695L7.96352 10.4963L11.61 9.52392L16.0612 8.34197C16.7402 8.14078 17.1341 7.4534 16.9581 6.78279Z\"\n                            fill=\"url(#paint0_linear_1362_130)\" />\n                    </g>\n                    <defs>\n                        <linearGradient id=\"paint0_linear_1362_130\" x1=\"17\" y1=\"16.3055\" x2=\"-0.334448\" y2=\"15.8973\"\n                            gradientUnits=\"userSpaceOnUse\">\n                            <stop stop-color=\"#57CD86\" />\n                            <stop offset=\"1\" stop-color=\"#3CC3D9\" />\n                        </linearGradient>\n                    </defs>\n                </svg>\n                <span class=\"aerial-search-history-card-flight-information-title\">{{destination}}</span>\n\n            </div>\n            <div class=\"aerial-search-history-card-row\">\n                <span>Saindo de </span>\n                <span class=\"aerial-search-history-card-flight-information-highlight\">{{origin}}</span>\n            </div>\n            <div class=\"aerial-search-history-card-row\">\n                <span>{{isOneWay ? 'em ' : 'de '}} </span>\n                <span class=\"aerial-search-history-card-flight-information-highlight\">{{goingDate}}</span>\n                <div *ngIf=\"isRoundTrip\" class=\"aerial-search-history-card-row\">\n                    <span> at\u00E9 </span>\n                    <span class=\"aerial-search-history-card-flight-information-highlight\">{{returningDate}}</span>\n                </div>\n            </div>\n        </div>\n        <span class=\"aerial-search-history-card-flight-information-link\">\n            <span *ngIf=\"aerialSearchHistory?.isInternational; else continuarPesquisa\" (click)=\"linkClick()\">\n                Falar com especialista\n            </span>\n\n            <ng-template #continuarPesquisa>\n                <span (click)=\"linkClick()\">\n                    Continuar pesquisa\n                </span>\n            </ng-template>\n        </span>\n    </div>\n</div>", styles: ["@import\"https://fonts.googleapis.com/css?family=Roboto:300,300i,400,400i,500,500i,700,700i,900,900i\";.aerial-search-history-card-row{gap:.25rem;display:flex;flex-direction:row;align-items:center}.aerial-search-history-card-row>img{height:1rem}.aerial-search-history-card{font-family:Roboto;font-weight:300;color:#8b8b8b;min-width:20rem;width:20rem;min-height:7.5rem;height:7.5rem;font-size:.875rem;display:flex;flex-direction:row;align-items:stretch;gap:.75rem;border-radius:1.25rem;background:#fff}.aerial-search-history-card>.aerial-search-history-card-image{height:100%;aspect-ratio:1;border-radius:1.25rem;overflow:hidden;position:relative;display:flex;align-items:center;justify-content:center}.aerial-search-history-card>.aerial-search-history-card-image>img{object-fit:cover;object-position:center center}.aerial-search-history-card>.aerial-search-history-card-information{margin:.75rem .75rem .75rem 0;display:flex;flex-direction:column;justify-content:space-between;align-items:flex-start;background:transparent}.aerial-search-history-card>.aerial-search-history-card-information>.aerial-search-history-card-flight-information{height:100%;gap:.375rem;display:flex;flex-direction:column;align-items:flex-start;background:transparent}.aerial-search-history-card>.aerial-search-history-card-information>.aerial-search-history-card-flight-information .aerial-search-history-card-flight-information-title{font-weight:400;font-size:1.125rem;color:#707070;padding-left:.25rem}.aerial-search-history-card>.aerial-search-history-card-information>.aerial-search-history-card-flight-information .aerial-search-history-card-flight-information-highlight{font-weight:400;color:#707070}.aerial-search-history-card>.aerial-search-history-card-information>.aerial-search-history-card-flight-information-link{font-weight:300;color:#0079be;cursor:pointer}\n"], dependencies: [{ kind: "directive", type: i1.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }, { kind: "directive", type: ImageFallbackDirective, selector: "[imageFallback]", inputs: ["imageFallback"] }, { kind: "pipe", type: DestinationImageUrlPipe, name: "destinationImageUrl" }], changeDetection: i0.ChangeDetectionStrategy.OnPush });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: AerialSearchHistoryComponent, decorators: [{
            type: Component,
            args: [{ selector: 'aerial-search-history', changeDetection: ChangeDetectionStrategy.OnPush, template: "<div class=\"aerial-search-history-card\">\n    <div class=\"aerial-search-history-card-image\">\n        <img [src]=\"destination | destinationImageUrl : aerialSearchHistory?.destinationCountry !== 'Brasil'\"\n            imageFallback=\"assets/images/image-fallback.webp\" style=\"height: 100%; width: 100%;\" />\n    </div>\n    <div class=\"aerial-search-history-card-information\">\n        <div class=\"aerial-search-history-card-flight-information\">\n            <div class=\"aerial-search-history-card-row\">\n                <svg width=\"17\" height=\"18\" viewBox=\"0 0 17 18\" fill=\"none\" xmlns=\"http://www.w3.org/2000/svg\">\n                    <mask id=\"mask0_1362_130\" style=\"mask-type:alpha\" maskUnits=\"userSpaceOnUse\" x=\"0\" y=\"0\" width=\"17\"\n                        height=\"18\">\n                        <rect y=\"0.5\" width=\"17\" height=\"17\" fill=\"#D9D9D9\" />\n                    </mask>\n                    <g mask=\"url(#mask0_1362_130)\">\n                        <path\n                            d=\"M0.553255 15.9672C0.553255 15.7804 0.704702 15.629 0.891521 15.629H6.14204C6.32885 15.629 6.4803 15.7804 6.4803 15.9672C6.4803 16.154 6.32885 16.3055 6.14204 16.3055H0.891521C0.704702 16.3055 0.553255 16.154 0.553255 15.9672ZM16.9581 6.78279C16.7821 6.11218 16.0863 5.70981 15.4157 5.89423L10.9645 7.08457L5.16372 1.69452L3.56263 2.12203L7.03305 8.1324L2.86687 9.24729L1.21548 7.95636L0 8.28329L1.52564 10.9322L2.17111 12.0471L3.51233 11.695L7.96352 10.4963L11.61 9.52392L16.0612 8.34197C16.7402 8.14078 17.1341 7.4534 16.9581 6.78279Z\"\n                            fill=\"url(#paint0_linear_1362_130)\" />\n                    </g>\n                    <defs>\n                        <linearGradient id=\"paint0_linear_1362_130\" x1=\"17\" y1=\"16.3055\" x2=\"-0.334448\" y2=\"15.8973\"\n                            gradientUnits=\"userSpaceOnUse\">\n                            <stop stop-color=\"#57CD86\" />\n                            <stop offset=\"1\" stop-color=\"#3CC3D9\" />\n                        </linearGradient>\n                    </defs>\n                </svg>\n                <span class=\"aerial-search-history-card-flight-information-title\">{{destination}}</span>\n\n            </div>\n            <div class=\"aerial-search-history-card-row\">\n                <span>Saindo de </span>\n                <span class=\"aerial-search-history-card-flight-information-highlight\">{{origin}}</span>\n            </div>\n            <div class=\"aerial-search-history-card-row\">\n                <span>{{isOneWay ? 'em ' : 'de '}} </span>\n                <span class=\"aerial-search-history-card-flight-information-highlight\">{{goingDate}}</span>\n                <div *ngIf=\"isRoundTrip\" class=\"aerial-search-history-card-row\">\n                    <span> at\u00E9 </span>\n                    <span class=\"aerial-search-history-card-flight-information-highlight\">{{returningDate}}</span>\n                </div>\n            </div>\n        </div>\n        <span class=\"aerial-search-history-card-flight-information-link\">\n            <span *ngIf=\"aerialSearchHistory?.isInternational; else continuarPesquisa\" (click)=\"linkClick()\">\n                Falar com especialista\n            </span>\n\n            <ng-template #continuarPesquisa>\n                <span (click)=\"linkClick()\">\n                    Continuar pesquisa\n                </span>\n            </ng-template>\n        </span>\n    </div>\n</div>", styles: ["@import\"https://fonts.googleapis.com/css?family=Roboto:300,300i,400,400i,500,500i,700,700i,900,900i\";.aerial-search-history-card-row{gap:.25rem;display:flex;flex-direction:row;align-items:center}.aerial-search-history-card-row>img{height:1rem}.aerial-search-history-card{font-family:Roboto;font-weight:300;color:#8b8b8b;min-width:20rem;width:20rem;min-height:7.5rem;height:7.5rem;font-size:.875rem;display:flex;flex-direction:row;align-items:stretch;gap:.75rem;border-radius:1.25rem;background:#fff}.aerial-search-history-card>.aerial-search-history-card-image{height:100%;aspect-ratio:1;border-radius:1.25rem;overflow:hidden;position:relative;display:flex;align-items:center;justify-content:center}.aerial-search-history-card>.aerial-search-history-card-image>img{object-fit:cover;object-position:center center}.aerial-search-history-card>.aerial-search-history-card-information{margin:.75rem .75rem .75rem 0;display:flex;flex-direction:column;justify-content:space-between;align-items:flex-start;background:transparent}.aerial-search-history-card>.aerial-search-history-card-information>.aerial-search-history-card-flight-information{height:100%;gap:.375rem;display:flex;flex-direction:column;align-items:flex-start;background:transparent}.aerial-search-history-card>.aerial-search-history-card-information>.aerial-search-history-card-flight-information .aerial-search-history-card-flight-information-title{font-weight:400;font-size:1.125rem;color:#707070;padding-left:.25rem}.aerial-search-history-card>.aerial-search-history-card-information>.aerial-search-history-card-flight-information .aerial-search-history-card-flight-information-highlight{font-weight:400;color:#707070}.aerial-search-history-card>.aerial-search-history-card-information>.aerial-search-history-card-flight-information-link{font-weight:300;color:#0079be;cursor:pointer}\n"] }]
        }], ctorParameters: function () { return [{ type: i0.ChangeDetectorRef }]; }, propDecorators: { aerialSearchHistory: [{
                type: Input
            }], continueSearch: [{
                type: Output
            }], continueService: [{
                type: Output
            }] } });

var AERIAL_TRIP_TYPE;
(function (AERIAL_TRIP_TYPE) {
    AERIAL_TRIP_TYPE["ONE_WAY"] = "ONE_WAY";
    AERIAL_TRIP_TYPE["ROUND_TRIP"] = "ROUND_TRIP";
    AERIAL_TRIP_TYPE["MULTI_TRIP"] = "MULTI_TRIP";
})(AERIAL_TRIP_TYPE || (AERIAL_TRIP_TYPE = {}));

class SearchHistoryService {
    constructor() {
        this._strategy = null;
    }
    setStrategy(strategy) {
        this._strategy = strategy;
    }
    fetch(product) {
        if (!this._strategy) {
            return throwError(() => new Error('Search history strategy not defined'));
        }
        return this._strategy.fetch(product);
    }
    save(item) {
        if (!this._strategy) {
            return throwError(() => new Error('Search history strategy not defined'));
        }
        return this._strategy.save(item);
    }
}
SearchHistoryService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: SearchHistoryService, deps: [], target: i0.ɵɵFactoryTarget.Injectable });
SearchHistoryService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: SearchHistoryService, providedIn: 'root' });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: SearchHistoryService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root',
                }]
        }] });

class LocalStorageService {
    constructor() { }
    setItem(key, value) {
        const jsonValue = JSON.stringify(value);
        localStorage.setItem(key, jsonValue);
    }
    getItem(key) {
        const item = localStorage.getItem(key);
        return item ? JSON.parse(item) : null;
    }
    removeItem(key) {
        localStorage.removeItem(key);
    }
    clear() {
        localStorage.clear();
    }
}
LocalStorageService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: LocalStorageService, deps: [], target: i0.ɵɵFactoryTarget.Injectable });
LocalStorageService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: LocalStorageService, providedIn: 'root' });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: LocalStorageService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root'
                }]
        }], ctorParameters: function () { return []; } });

class SearchHistoryLocalStorageManager {
    constructor(localStorageService) {
        this.localStorageService = localStorageService;
        this._searchHistoryLocalStorageKey = 'zupper-search-history';
    }
    getAll() {
        const searchHistory = this.localStorageService.getItem(this._searchHistoryLocalStorageKey) || [];
        const futureSearches = searchHistory
            .map((history) => {
            const searchHistory = new AerialSearchHistory(history);
            return searchHistory;
        })
            .filter((search) => moment$1(search.departureDate).isAfter(moment$1()));
        return futureSearches.slice(0, 4);
    }
    save(history) {
        const formattedHistory = AerialSearchHistory.fromAerialSearchHistory(history);
        let allRecords = this.getAll() || [];
        const firstSlice = formattedHistory.slices[0] ?? {};
        const index = allRecords.findIndex((record) => record.slices?.some((slice) => slice.origin === firstSlice.origin &&
            slice.destination === firstSlice.destination));
        const goingDate = moment$1(firstSlice.goingDate);
        if (index !== -1) {
            const existingDate = allRecords[index].slices[0].goingDate;
            if (goingDate.isValid() && goingDate.isSameOrAfter(existingDate)) {
                allRecords.splice(index, 1);
                allRecords.unshift(formattedHistory);
            }
        }
        else if (goingDate.isValid() && goingDate.isSameOrAfter(moment$1())) {
            allRecords.unshift(formattedHistory);
        }
        allRecords = allRecords.slice(0, 4);
        this.localStorageService.setItem(this._searchHistoryLocalStorageKey, allRecords.map((record) => record.toApi()));
    }
}
SearchHistoryLocalStorageManager.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: SearchHistoryLocalStorageManager, deps: [{ token: LocalStorageService }], target: i0.ɵɵFactoryTarget.Injectable });
SearchHistoryLocalStorageManager.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: SearchHistoryLocalStorageManager, providedIn: 'root' });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: SearchHistoryLocalStorageManager, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root',
                }]
        }], ctorParameters: function () { return [{ type: LocalStorageService }]; } });

class ApiSearchHistoryService {
    constructor(http, config) {
        this.http = http;
        this.config = config;
        this.baseUrl = config?.baseApiUrl ?? '';
    }
    getUserHistory(customerSessionId) {
        const url = `${this.baseUrl}/rest/v2/aerial/latestSearches?customerSessionId=${customerSessionId}`;
        return this.http.get(url);
    }
}
ApiSearchHistoryService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: ApiSearchHistoryService, deps: [{ token: i1$1.HttpClient }, { token: 'SEARCH_HISTORY_CONFIG' }], target: i0.ɵɵFactoryTarget.Injectable });
ApiSearchHistoryService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: ApiSearchHistoryService, providedIn: 'root' });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: ApiSearchHistoryService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root',
                }]
        }], ctorParameters: function () { return [{ type: i1$1.HttpClient }, { type: undefined, decorators: [{
                    type: Inject,
                    args: ['SEARCH_HISTORY_CONFIG']
                }] }]; } });

class LoggedInUserSearchHistoryStrategyService {
    constructor(apiSearchHistoryService) {
        this.apiSearchHistoryService = apiSearchHistoryService;
    }
    fetch(product) {
        if (!this.customerSessionId) {
            return throwError(() => new Error('Customer session ID is not set.'));
        }
        return this.apiSearchHistoryService
            .getUserHistory(this.customerSessionId)
            .pipe(map((response) => {
            if (!response.success) {
                throw new Error(response.error.join(', '));
            }
            return response.result.map((item) => this.convertToSearchHistoryInterface(item, product));
        }));
    }
    convertToSearchHistoryInterface(apiSearchHistory, product) {
        const searchHistory = new AerialSearchHistory(apiSearchHistory);
        const travelerGroups = [];
        this.createAndFillTravelerGroup(travelerGroups, apiSearchHistory.adultQuantity, TRAVELER_TYPE.ADULT);
        this.createAndFillTravelerGroup(travelerGroups, apiSearchHistory.childrenQuantity, TRAVELER_TYPE.CHILD);
        this.createAndFillTravelerGroup(travelerGroups, apiSearchHistory.infantQuantity, TRAVELER_TYPE.INFANT);
        searchHistory.travelerGroups = travelerGroups;
        searchHistory.product = product;
        searchHistory.addTravelerGroup = (group) => {
            travelerGroups.push(group);
        };
        return searchHistory;
    }
    createAndFillTravelerGroup(groups, quantity, type) {
        if (quantity > 0) {
            const group = new TravelerGroup();
            for (let i = 0; i < quantity; i++) {
                group.addTraveler({ travelerType: type });
            }
            groups.push(group);
        }
    }
    save(data) {
        return of();
    }
    setCustomerSessionId(customerSessionId) {
        this.customerSessionId = customerSessionId;
    }
}
LoggedInUserSearchHistoryStrategyService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: LoggedInUserSearchHistoryStrategyService, deps: [{ token: ApiSearchHistoryService }], target: i0.ɵɵFactoryTarget.Injectable });
LoggedInUserSearchHistoryStrategyService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: LoggedInUserSearchHistoryStrategyService, providedIn: 'root' });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: LoggedInUserSearchHistoryStrategyService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root',
                }]
        }], ctorParameters: function () { return [{ type: ApiSearchHistoryService }]; } });

class LoggedOutUserSearchHistoryStrategyService {
    constructor(localStorageManager) {
        this.localStorageManager = localStorageManager;
    }
    fetch() {
        const history = this.localStorageManager.getAll() || [];
        return of(history);
    }
    save(data) {
        const savedData = this.localStorageManager.save(data);
        return of();
    }
}
LoggedOutUserSearchHistoryStrategyService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: LoggedOutUserSearchHistoryStrategyService, deps: [{ token: SearchHistoryLocalStorageManager }], target: i0.ɵɵFactoryTarget.Injectable });
LoggedOutUserSearchHistoryStrategyService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: LoggedOutUserSearchHistoryStrategyService, providedIn: 'root' });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: LoggedOutUserSearchHistoryStrategyService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root',
                }]
        }], ctorParameters: function () { return [{ type: SearchHistoryLocalStorageManager }]; } });

class SearchHistoryStrategyFactoryService {
    constructor(loggedInStrategy, loggedOutStrategy) {
        this.loggedInStrategy = loggedInStrategy;
        this.loggedOutStrategy = loggedOutStrategy;
    }
    getStrategy(customerSessionId) {
        if (customerSessionId) {
            this.loggedInStrategy.setCustomerSessionId(customerSessionId);
            return this.loggedInStrategy;
        }
        else {
            return this.loggedOutStrategy;
        }
    }
}
SearchHistoryStrategyFactoryService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: SearchHistoryStrategyFactoryService, deps: [{ token: LoggedInUserSearchHistoryStrategyService }, { token: LoggedOutUserSearchHistoryStrategyService }], target: i0.ɵɵFactoryTarget.Injectable });
SearchHistoryStrategyFactoryService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: SearchHistoryStrategyFactoryService, providedIn: 'root' });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: SearchHistoryStrategyFactoryService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root'
                }]
        }], ctorParameters: function () { return [{ type: LoggedInUserSearchHistoryStrategyService }, { type: LoggedOutUserSearchHistoryStrategyService }]; } });

class ZupperSearchHistoryModule {
    static forRoot(config) {
        return {
            ngModule: ZupperSearchHistoryModule,
            providers: [{ provide: 'SEARCH_HISTORY_CONFIG', useValue: config }],
        };
    }
}
ZupperSearchHistoryModule.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: ZupperSearchHistoryModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule });
ZupperSearchHistoryModule.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "15.2.10", ngImport: i0, type: ZupperSearchHistoryModule, declarations: [DestinationImageUrlPipe,
        ImageFallbackDirective,
        AerialSearchHistoryComponent], imports: [CommonModule], exports: [DestinationImageUrlPipe,
        AerialSearchHistoryComponent,
        ImageFallbackDirective] });
ZupperSearchHistoryModule.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: ZupperSearchHistoryModule, imports: [CommonModule] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: ZupperSearchHistoryModule, decorators: [{
            type: NgModule,
            args: [{
                    declarations: [
                        DestinationImageUrlPipe,
                        ImageFallbackDirective,
                        AerialSearchHistoryComponent,
                    ],
                    imports: [CommonModule],
                    exports: [
                        DestinationImageUrlPipe,
                        AerialSearchHistoryComponent,
                        ImageFallbackDirective,
                    ],
                }]
        }] });

/*
 * Public API Surface of zupper-search-history
 */
//COMPONENTS

/**
 * Generated bundle index. Do not edit.
 */

export { AERIAL_TRIP_TYPE, AerialSearchHistory, AerialSearchHistoryComponent, AerialSearchHistorySlice, DestinationImageUrlPipe, ImageFallbackDirective, SearchHistoryLocalStorageManager, SearchHistoryService, SearchHistoryStrategyFactoryService, TRAVELER_TYPE, ZupperProduct, ZupperSearchHistoryModule };
